import { useEffect, useMemo, useState } from 'react';
import type { Dish, Meal, PlanSelection } from '../types';
import { dishCategories, dishCategory, matchesDishSearch } from '../lib/dish-categories';
import { snapshotIngredients } from '../lib/calculation';
import { Modal } from './Modal';
import { CheckIcon, SearchIcon, UtensilsIcon, XIcon } from './Icons';

interface Props {
  open: boolean;
  dateLabel: string;
  meal: Meal;
  mealLabel: string;
  dishes: Dish[];
  selections: PlanSelection[];
  onClose: () => void;
  onApply: (selections: PlanSelection[]) => void;
}

function initialDraft(selections: PlanSelection[]): Record<string, number> {
  return Object.fromEntries(selections.map((selection) => [selection.dishId, selection.batchCount]));
}

export function DishSelector({ open, dateLabel, meal, mealLabel, dishes, selections, onClose, onApply }: Props) {
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('全部');
  const [draft, setDraft] = useState<Record<string, number>>({});
  const activeDishes = useMemo(() => dishes.filter((dish) => dish.active), [dishes]);
  const availableDishes = useMemo(() => {
    const selectedIds = new Set(selections.map((selection) => selection.dishId));
    return dishes.filter((dish) => dish.active || selectedIds.has(dish.id));
  }, [dishes, selections]);
  const categories = useMemo(() => dishCategories(activeDishes), [activeDishes]);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setCategory('全部');
    setDraft(initialDraft(selections));
  }, [open, meal, dateLabel]);

  const selectedIds = Object.keys(draft);
  const selectedDishes = selectedIds
    .map((id) => availableDishes.find((dish) => dish.id === id))
    .filter((dish): dish is Dish => Boolean(dish));
  const filtered = activeDishes.filter((dish) => {
    const categoryMatches = category === '全部' || (category === '已选' ? draft[dish.id] !== undefined : dishCategory(dish) === category);
    return categoryMatches && matchesDishSearch(dish, query);
  });

  const toggle = (dish: Dish) => {
    setDraft((current) => {
      const next = { ...current };
      if (next[dish.id] !== undefined) delete next[dish.id];
      else next[dish.id] = 1;
      return next;
    });
  };

  const apply = () => {
    const next = selectedIds.flatMap((dishId) => {
      const existing = selections.find((selection) => selection.dishId === dishId);
      if (existing) return [{ ...existing, batchCount: draft[dishId] }];
      const dish = dishes.find((item) => item.id === dishId && item.active);
      if (!dish) return [];
      return [{
        dishId,
        meal,
        batchCount: draft[dishId],
        recipeSnapshot: { dishName: dish.name, ingredients: snapshotIngredients(dish) },
      }];
    });
    onApply(next);
    onClose();
  };

  return (
    <Modal open={open} title={`选择${mealLabel}菜品`} eyebrow={`${dateLabel} · 可多选`} size="large" onClose={onClose}>
      <div className="dish-selector">
        <div className="dish-selector__search">
          <SearchIcon size={19} />
          <input
            aria-label="搜索菜品"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="搜菜品"
          />
          {query && <button type="button" onClick={() => setQuery('')} aria-label="清空搜索"><XIcon size={16} /></button>}
        </div>

        <div className="dish-selector__categories" role="tablist" aria-label="菜品分类">
          {['全部', '已选', ...categories].map((item) => (
            <button
              role="tab"
              aria-selected={category === item}
              className={category === item ? 'is-active' : ''}
              type="button"
              key={item}
              onClick={() => setCategory(item)}
            >
              {item}<span>{item === '全部' ? activeDishes.length : item === '已选' ? selectedIds.length : activeDishes.filter((dish) => dishCategory(dish) === item).length}</span>
            </button>
          ))}
        </div>

        <section className="dish-selector__selected" aria-label="已选菜品">
          <header>
            <div><strong>已选 {selectedIds.length} 道菜</strong><span>默认按 50 人用量，返回计划页可调整</span></div>
            {selectedIds.length > 0 && <button className="text-button" type="button" onClick={() => setDraft({})}>清空已选</button>}
          </header>
          {selectedDishes.length ? (
            <div className="dish-selector__selected-list">
              {selectedDishes.map((dish) => (
                <div className="selected-dish-pill" key={dish.id}>
                  <span>{dish.name}{!dish.active && <small>已停用</small>}</span>
                  <button type="button" onClick={() => toggle(dish)} aria-label={`取消选择${dish.name}`}><XIcon size={14} /></button>
                </div>
              ))}
            </div>
          ) : <p>还没有选择菜品，可从下方勾选。</p>}
        </section>

        <div className="dish-selector__result-heading">
          <strong>{category === '全部' ? '全部菜品' : category === '已选' ? '本餐已选菜品' : category}</strong>
          <span aria-live="polite">找到 {filtered.length} 道菜</span>
        </div>
        {filtered.length ? (
          <div className="dish-selector__grid" role="list" aria-label="可选菜品">
            {filtered.map((dish) => {
              const selected = draft[dish.id] !== undefined;
              return (
                <article className={`selector-dish-card ${selected ? 'is-selected' : ''}`} key={dish.id} role="listitem">
                  <button
                    className="selector-dish-card__toggle"
                    type="button"
                    aria-label={`${selected ? '取消选择' : '选择'}${dish.name}`}
                    aria-pressed={selected}
                    onClick={() => toggle(dish)}
                  >
                    <span className="selector-dish-card__check">{selected ? <CheckIcon size={15} /> : <span />}</span>
                    <span className="selector-dish-card__copy">
                      <strong>{dish.name}</strong>
                      <small>{dishCategory(dish)} · {dish.ingredients.slice(0, 3).map((item) => item.name).join('、')}{dish.ingredients.length > 3 ? '…' : ''}</small>
                    </span>
                  </button>
                </article>
              );
            })}
          </div>
        ) : (
          <div className="dish-selector__empty"><SearchIcon size={28} /><strong>没有找到匹配的菜品</strong><p>换个关键词或分类试试。</p></div>
        )}

        <footer className="dish-selector__footer">
          <span><UtensilsIcon size={17} /> 已选 <strong>{selectedIds.length}</strong> 道</span>
          <div><button className="button button--ghost" type="button" onClick={onClose}>取消</button><button className="button button--primary" type="button" onClick={apply}>完成选菜（{selectedIds.length}）</button></div>
        </footer>
      </div>
    </Modal>
  );
}
